import React, { InputHTMLAttributes, useState, ChangeEvent, FC, useEffect, useMemo, ReactNode } from 'react'
import { nanoid } from 'nanoid'
import Label from '@app/components/Label'
import Cleave from 'cleave.js/react'
import { Icon } from '@iconify/react'
import S from './TextField.styles'
import FieldError from '../FieldError'

interface Mask {
  prefix?: string
  blocks?: number[]
  delimiter?: string
  numericOnly?: boolean
  uppercase?: boolean
}

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  /** Pass if you want an initiated component value */
  defaultValue?: string
  /** Pass if you want a controlled component */
  value?: string
  /** Optional error component to render */
  error?: string
  /** Optional mask for input */
  mask?: Mask
  /** Optionally return raw value from edit() */
  returnRawValue?: boolean
  /** optional hint to display between the label and the input */
  hint?: ReactNode
  /** Optional callback to allow clearing the input */
  onClear?: () => void
}

const TextField: FC<TextFieldProps> = ({
  label,
  type = 'text',
  name = nanoid(),
  placeholder,
  defaultValue = '',
  value = '',
  onChange,
  onBlur,
  onClear,
  error,
  mask,
  returnRawValue = false,
  hint,
}) => {
  const [val, setVal] = useState(value ?? defaultValue)

  const classNames = useMemo(() => {
    const classes = ['']
    if (error) classes.push('hasError')
    if (onClear) classes.push('withClear')
    return classes.join(' ')
  }, [error, onClear])

  const edit = (ev: ChangeEvent<HTMLInputElement>) => {
    const target = ev.target as EventTarget & { rawValue?: string }
    let newValue = ev.currentTarget.value

    // Override the value if ev.target when returnRawValue is TRUE
    // So that the onChange callback can receive the intended value
    if (returnRawValue && target.rawValue) {
      newValue = target.rawValue
      ev.target.value = newValue
    }

    setVal(newValue)
    onChange?.(ev)
  }

  const clear = () => {
    setVal('')
    onClear?.()
  }

  useEffect(() => {
    setVal(value)
  }, [value])

  return (
    <S.TextField>
      {label && <Label htmlFor={name}>{label}</Label>}
      {hint && <S.Hint>{hint}</S.Hint>}
      <S.InputWrapper>
        {mask ? (
          <Cleave
            options={mask}
            className={classNames}
            id={name}
            type={type}
            name={name}
            value={val}
            placeholder={placeholder}
            onChange={edit}
            onBlur={onBlur}
          />
        ) : (
          <S.Input
            className={classNames}
            id={name}
            type={type}
            name={name}
            value={val}
            placeholder={placeholder}
            onChange={edit}
            onBlur={onBlur}
          />
        )}
        {onClear && val && (
          <S.Clear onClick={clear}>
            <Icon icon="bi:x" fontSize={20} />
          </S.Clear>
        )}
      </S.InputWrapper>
      <FieldError show={!!error} message={error} />
    </S.TextField>
  )
}

export default TextField
